import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { IGasContract, IFetchContractsResponse, ContractModificationTypes } from '../types/contracts';
import { UseRefCatastralState } from '../types/refCatastral';
import { IResPartnerAddress, UseAddressState } from '../types/address';
import { UseInvoiceOnlineState, InvoiceOnlineTypes } from '../types/invoiceOnline';
import { NoSIPSCessionStates } from '../types/noSIPSCession';


export interface IGasContractsState {
  status: 'uninitialized' | 'loading' | 'succeeded' | 'failed';
  downloading_contract: boolean;
  filterContract?: string;
  view_contract?: IGasContract["id"];
  error_code?: string;
  items: IGasContract[],
  total_items: number;
  offset_items: number;
  page_items: number;
  addressess: {
    available: IResPartnerAddress[];
    status: Record<IGasContract["id"], {
      error_code?: string;
      status: UseAddressState;
    }>
  };
  refCatastral: {
    status: Record<IGasContract["id"], {
      error_code?: string;
      status: UseRefCatastralState;
    }>
  };
  invoiceOnline: {
    status: Record<IGasContract["id"], {
      error_code?: string;
      status: UseInvoiceOnlineState;
    }>
  };
}

const initialState: IGasContractsState = {
  status: 'uninitialized',
  downloading_contract: false,
  items: [],
  total_items: 0,
  offset_items: 0,
  page_items: 0,
  addressess: {
    available: [],
    status: {}
  },
  refCatastral: {
    status: {}
  },
  invoiceOnline: {
    status: {}
  },
};

export const gasContractsSlice = createSlice({
  name: 'gas',
  initialState,
  reducers: {
    receivedContracts: (state, action: PayloadAction<IFetchContractsResponse<IGasContract>>) => {
      state.total_items = action.payload.n_items;
      state.offset_items = action.payload.offset;
      state.page_items = action.payload.limit;
      state.items = action.payload.items;
      state.status = 'succeeded';
    },
    fetchContractsRequested: state => {
      state.status = 'loading';
    },
    filterContractsRequested: (state, action: PayloadAction<IGasContractsState["filterContract"]>) => {
      state.filterContract = action.payload;
    },
    filterContractsCleared: state => {
      state.filterContract = undefined;
    },
    exportContractRequested: state => {
      state.downloading_contract = true;
    },
    exportContractDone: state => {
      state.downloading_contract = false;
    },
    viewContractSet: (state, action: PayloadAction<IGasContractsState["view_contract"]>) => {
      state.view_contract = action.payload;
    },
    phoneSet: (state, action: PayloadAction<IGasContract["notification_address"]["phone"]>) => {
      if (state.view_contract) {
        state.items.find(
          c => c.id === state.view_contract
        )!.notification_address.phone = action.payload;
      }
    },
    emailSet: (state, action: PayloadAction<IGasContract["notification_address"]["email"]>) => {
      if (state.view_contract) {
        state.items.find(
          c => c.id === state.view_contract
        )!.notification_address.email = action.payload;
      }
    },
    signatureStarted: (state, action: PayloadAction<ContractModificationTypes>) => {
      state.items.find(
          c => c.id === state.view_contract
        )!.waitingForSignature?.push(action.payload);
    },
    refCatastralChanged: (state, action: PayloadAction<string>) => {
      if (state.view_contract) {
        const contract = state.items.find(
          c => c.id === state.view_contract
        )!.ref_catastral = action.payload;
      }
    },
    refCatastralFormOpened: (state) => {
      state.refCatastral.status[state.view_contract!] = {status: "uninitialized"}
    },
    refCatastralFormFailed: (state) => {
      state.refCatastral.status[state.view_contract!] = {status: "error"}
    },
    refCatastralFormSubmited: (state) => {
      state.refCatastral.status[state.view_contract!] = {status: "submitting"}
    },
    refCatastralFormSuccessed: (state) => {
      state.refCatastral.status[state.view_contract!] = {status: "success"}
    },
    refCatastralFormClosed: (state) => {
      state.refCatastral.status[state.view_contract!] = {status: "uninitialized"}
    },
    recievedAddress: (state, action: PayloadAction<IGasContractsState["addressess"]["available"]>) => {
      state.addressess.available = action.payload
    },
    notificationFormOpened: (state) => {
      state.addressess.status[state.view_contract!] = {status: "uninitialized"}
    },
    notificationFormFailed: (state) => {
      state.addressess.status[state.view_contract!] = {status: "error"}
    },
    notificationFormSubmited: (state) => {
      state.addressess.status[state.view_contract!] = {status: "submitting"}
    },
    notificationFormClosed: (state) => {
      state.addressess.status[state.view_contract!] = {status: "uninitialized"}
    },
    addressFetching: (state) => {
      state.addressess.status[state.view_contract!] = {status: "fetching"}
    },
    notificationAddressUpdated: (state, action: PayloadAction<number>) => {
      if (state.view_contract) {
        // Buscar l'adreça disponible que s'ha seleccionat
        const selectedAddress = state.addressess.available.find(
          address => address.id === action.payload
        )

        // Assignar el IResPartnerAddress al notification_address
        if (selectedAddress){
          state.items.find(
            c => c.id === state.view_contract
          )!.notification_address = selectedAddress;
        }
      }

      state.addressess.status[state.view_contract!].status = "success";
    },
    notificationAddressCreated: (state, action: PayloadAction<IResPartnerAddress>) => {
      state.addressess.available.push(action.payload)
      state.items.find(
        c => c.id === state.view_contract
      )!.notification_address = action.payload;

      state.addressess.status[state.view_contract!].status = "success";
    },
    invoiceOnlineChanged: (state, action: PayloadAction<InvoiceOnlineTypes>) => {
      if (state.view_contract) {
        const contract = state.items.find(
          c => c.id === state.view_contract
        )!.invoice_online = action.payload;
      }
    },
    invoiceOnlineFormOpened: (state) => {
      state.invoiceOnline.status[state.view_contract!] = {status: "uninitialized"}
    },
    invoiceOnlineFormFailed: (state) => {
      state.invoiceOnline.status[state.view_contract!] = {status: "error"}
    },
    invoiceOnlineFormSubmited: (state) => {
      state.invoiceOnline.status[state.view_contract!] = {status: "submitting"}
    },
    invoiceOnlineFormSuccessed: (state) => {
      state.invoiceOnline.status[state.view_contract!] = {status: "success"}
    },
    invoiceOnlineFormClosed: (state) => {
      state.invoiceOnline.status[state.view_contract!] = {status: "uninitialized"}
    },
    setNoSIPSCessionState: (state, action: PayloadAction<NoSIPSCessionStates>) => {
      if (state.view_contract !== -1 ) {
        const contractIndex = state.items.findIndex(
          c => c.id === state.view_contract
        )
        if (contractIndex != -1 ) {
          state.items[contractIndex].no_cession_sips.state = action.payload;
        }
      }
    },
    setNoSIPSCessionIsWaitingForSignature: (state, action: PayloadAction<boolean>) => {
      if (state.view_contract !== -1 ) {
        const contractIndex = state.items.findIndex(
          c => c.id === state.view_contract
        )
        if (contractIndex != -1 ) {
          state.items[contractIndex].no_cession_sips.waiting_for_signature = action.payload;
        }
      }
    },
  }
})

export const actions = gasContractsSlice.actions;

export default gasContractsSlice.reducer;
